/**
 * Silva City project map — Leaflet (vanilla, imperative).
 *
 * We bypass `react-leaflet` because of a known React 18 StrictMode
 * double-mount issue ("Map container is already initialized"). Using the
 * Leaflet JS API directly in a `useEffect` gives us:
 *   - Total control over (un)mount lifecycle
 *   - A clean cleanup pass that removes the map and unbinds the DOM ref
 *   - Custom layered polygon styling for the animated "running border"
 */

import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { Globe } from "lucide-react";
import {
  SILVA_CITY_POLYGON,
  SILVA_CITY_BOUNDS,
  SILVA_CITY_PIN,
  SILVA_CITY_DEFAULT_ZOOM,
} from "./silvaCityKml";

const SilvaCityProjectMap = () => {
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    // If a stale leaflet id is on the DOM (e.g. from a previous mount),
    // drop it so a fresh init succeeds.
    if (node._leaflet_id != null) {
      try {
        delete node._leaflet_id;
      } catch (err) {
        // In rare browser/runtime combos, deleting a non-configurable property
        // can throw. The map will still try to initialize; if it errors we
        // surface it. Logged for diagnostics.
        console.warn("[SilvaCityProjectMap] could not clear stale _leaflet_id:", err);
      }
    }

    const center = [
      (SILVA_CITY_BOUNDS[0][0] + SILVA_CITY_BOUNDS[1][0]) / 2,
      (SILVA_CITY_BOUNDS[0][1] + SILVA_CITY_BOUNDS[1][1]) / 2,
    ];

    const map = L.map(node, {
      center,
      zoom: SILVA_CITY_DEFAULT_ZOOM,
      zoomControl: false,
      attributionControl: true,
      scrollWheelZoom: true,
      preferCanvas: false,
    });
    mapInstanceRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19,
      }
    ).addTo(map);

    // Zoom control on top right
    L.control.zoom({ position: "topright" }).addTo(map);

    // ---- POLYGON LAYERS ----

    // Soft gold fill
    L.polygon(SILVA_CITY_POLYGON, {
      color: "rgba(212,175,55,0)",
      fillColor: "#D4AF37",
      fillOpacity: 0.14,
      weight: 0,
      interactive: false,
    }).addTo(map);

    // Glow under-stroke (wide, blurred via CSS class)
    L.polygon(SILVA_CITY_POLYGON, {
      color: "#D4AF37",
      weight: 12,
      opacity: 0.18,
      fill: false,
      lineCap: "round",
      lineJoin: "round",
      interactive: false,
      className: "silva-glow-stroke",
    }).addTo(map);

    // Marching ants stroke
    const marchPoly = L.polygon(SILVA_CITY_POLYGON, {
      color: "#FFD86B",
      weight: 3,
      opacity: 0.95,
      fill: false,
      dashArray: "10 14",
      lineCap: "round",
      lineJoin: "round",
      interactive: false,
      className: "silva-march-stroke",
    }).addTo(map);

    // Top-most thin white line for definition
    L.polygon(SILVA_CITY_POLYGON, {
      color: "#FFFFFF",
      weight: 0.75,
      opacity: 0.45,
      fill: false,
      interactive: false,
    }).addTo(map);

    // Fit the bounds with padding
    map.fitBounds(L.latLngBounds(SILVA_CITY_BOUNDS), {
      padding: [40, 40],
      maxZoom: 17,
      animate: true,
    });

    // Animated drop-pin via DivIcon
    const pinIcon = L.divIcon({
      className: "silva-pin-leaf-icon",
      html: `
        <div class="silva-map-pin" data-testid="map-pin-marker">
          <span class="silva-pin-ping silva-pin-ping-1"></span>
          <span class="silva-pin-ping silva-pin-ping-2"></span>
          <span class="silva-pin-core">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </span>
        </div>
      `,
      iconSize: [60, 60],
      iconAnchor: [30, 60],
    });
    L.marker(SILVA_CITY_PIN, {
      icon: pinIcon,
      interactive: false,
      keyboard: false,
    }).addTo(map);

    // ---- Marching ants animation via stroke-dashoffset ----
    let raf;
    let offset = 0;
    const tick = () => {
      offset = (offset - 0.7) % 1000;
      const el = marchPoly.getElement();
      if (el) {
        el.setAttribute("stroke-dashoffset", String(offset));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // ---- Cleanup ----
    return () => {
      cancelAnimationFrame(raf);
      try {
        map.remove();
      } catch (err) {
        console.warn("[SilvaCityProjectMap] map.remove() failed:", err);
      }
      if (node && node._leaflet_id != null) {
        try {
          delete node._leaflet_id;
        } catch (err) {
          console.warn(
            "[SilvaCityProjectMap] could not clear _leaflet_id on cleanup:",
            err
          );
        }
      }
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <div
      data-testid="contact-map-canvas"
      className="relative w-full bg-black overflow-hidden h-[440px] sm:h-[500px] md:h-[560px] lg:aspect-[16/9] lg:h-auto"
    >
      {/* Leaflet root div */}
      <div
        ref={containerRef}
        className="absolute inset-0 silva-leaflet-dark"
        style={{ width: "100%", height: "100%" }}
      />

      {/* Floating glassmorphic label (top-left, animated) — HIDDEN on small screens
          to keep the map visible. Site location header above the map already
          shows the same info on mobile. */}
      <motion.div
        data-testid="map-pin-label"
        initial={{ opacity: 0, x: -16, y: -16 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.4, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute top-4 left-4 max-w-[320px] sm:max-w-[360px] z-[600] hidden md:block"
      >
        <div className="glass-strong neon-border rounded-2xl border px-5 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--sp-gold)] mr-2 align-middle animate-pulse" />
            66 ACRE PROJECT
          </p>
          <p className="mt-1.5 text-sm font-medium text-white leading-snug">
            Silva City By Seetai Properties Pvt Ltd.
          </p>
          <p className="mt-1 text-[11px] text-white/65 leading-relaxed">
            Airport Link Road, Jejuri Road, Bhandgaon, Maharashtra 412214
          </p>
        </div>
      </motion.div>

      {/* Coordinates badge (bottom-left, animated) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.55 }}
        className="pointer-events-none absolute bottom-4 left-4 z-[600] inline-flex items-center gap-2 rounded-full bg-black/65 border border-[color:rgba(212,175,55,0.4)] px-3 py-1.5 backdrop-blur-md"
      >
        <Globe className="h-3 w-3 text-[color:var(--sp-gold)]" />
        <span className="text-[10px] tracking-[0.28em] uppercase text-white/85">
          18.4125° N · 74.3253° E
        </span>
      </motion.div>

      {/* Boundary legend badge (bottom-right) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.55 }}
        className="pointer-events-none absolute bottom-4 right-4 z-[600] inline-flex items-center gap-2 rounded-full bg-black/65 border border-white/15 px-3 py-1.5 backdrop-blur-md"
      >
        <span className="block h-2 w-6 rounded-full bg-[color:var(--sp-gold)] silva-legend-pulse" />
        <span className="text-[10px] tracking-[0.28em] uppercase text-white/80">
          Site Boundary · 66 Acres
        </span>
      </motion.div>

      <style>{`
        .silva-leaflet-dark {
          background-color: #050808;
          font-family: Montserrat, system-ui, sans-serif;
        }
        .leaflet-container.silva-leaflet-dark {
          background-color: #050808;
        }
        .leaflet-container .leaflet-control-zoom a {
          background-color: rgba(10,10,10,0.85);
          color: #e0d7c0;
          border: 1px solid rgba(212,175,55,0.35);
        }
        .leaflet-container .leaflet-control-zoom a:hover {
          background-color: rgba(212,175,55,0.18);
          color: #D4AF37;
        }
        .leaflet-control-attribution {
          background: rgba(0,0,0,0.55) !important;
          color: rgba(255,255,255,0.55) !important;
          font-size: 9px !important;
          padding: 2px 6px !important;
        }
        .leaflet-control-attribution a { color: #D4AF37 !important; }

        /* Polygon stroke effects */
        .silva-march-stroke {
          filter: drop-shadow(0 0 6px rgba(212,175,55,0.7));
        }
        .silva-glow-stroke {
          filter: blur(2.5px);
        }

        /* Pin marker styles */
        .silva-pin-leaf-icon {
          background: transparent !important;
          border: none !important;
        }
        .silva-map-pin {
          position: relative;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          animation: silvaPinDrop 0.9s cubic-bezier(.22,1,.36,1) 0.3s both;
          pointer-events: none;
        }
        .silva-map-pin .silva-pin-core {
          position: relative;
          width: 38px; height: 38px;
          border-radius: 999px;
          background: #D4AF37;
          color: #0a0a0a;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 10px 20px rgba(0,0,0,0.5), 0 0 22px rgba(212,175,55,0.55);
          animation: silvaPinFloat 2.4s ease-in-out infinite;
        }
        .silva-map-pin .silva-pin-core svg { width: 20px; height: 20px; }
        .silva-pin-ping {
          position: absolute;
          left: 50%;
          bottom: 10px;
          transform: translate(-50%, 50%);
          width: 38px; height: 38px;
          border-radius: 999px;
          background: rgba(212,175,55,0.55);
          opacity: 0;
          animation: silvaPinPing 2.2s cubic-bezier(0,0,.2,1) infinite;
        }
        .silva-pin-ping.silva-pin-ping-2 { animation-delay: 1.1s; }
        @keyframes silvaPinDrop {
          0% { transform: translateY(-120px); opacity: 0; }
          70% { transform: translateY(6px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes silvaPinFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes silvaPinPing {
          0% { transform: translate(-50%, 50%) scale(0.5); opacity: 0.7; }
          80% { transform: translate(-50%, 50%) scale(2.2); opacity: 0; }
          100% { transform: translate(-50%, 50%) scale(2.4); opacity: 0; }
        }
        .silva-legend-pulse {
          animation: silvaLegendPulse 1.6s ease-in-out infinite;
        }
        @keyframes silvaLegendPulse {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SilvaCityProjectMap;
